import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

/**
 * Tour hook for Business List page (Member & HRM view)
 * Shows key features of the business trip management interface
 */
export const useBusinessListTour = (pageType: 'member' | 'hrm') => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getTourSteps = (): TourStep[] => {
        const commonSteps: TourStep[] = [
            {
                selector: '[data-tour="business-list"]',
                title: 'Quản lý công tác',
                description:
                    pageType === 'hrm'
                        ? 'Trang quản lý toàn bộ đơn công tác của nhân viên.\n• Duyệt đơn công tác\n• Theo dõi thống kê\n• Xuất báo cáo'
                        : 'Trang quản lý đơn công tác của bạn.\n• Tạo đơn công tác\n• Theo dõi trạng thái\n• Xem lịch sử',
            },
            {
                selector: '[data-tour="business-filter-bar"]',
                title: 'Bộ lọc đơn công tác',
                description:
                    'Lọc đơn công tác theo nhiều tiêu chí:\n• Trạng thái (chờ duyệt, đã duyệt, từ chối)\n• Loại công tác\n• Thời gian\n• Phòng ban',
            },
            {
                selector: '[data-tour="business-search"]',
                title: 'Tìm kiếm nhanh',
                description:
                    'Tìm kiếm đơn công tác theo:\n• Tên nhân viên\n• Phòng ban\n• Loại công tác\n• Địa điểm',
            },
            {
                selector: '[data-tour="business-data-table"]',
                title: 'Bảng dữ liệu đơn công tác',
                description:
                    'Bảng hiển thị danh sách đơn công tác:\n• Click để xem chi tiết\n• Sắp xếp theo cột\n• Thao tác với từng đơn',
            },
            {
                selector: '[data-tour="business-settings"]',
                title: 'Cài đặt bảng',
                description:
                    pageType === 'hrm'
                        ? 'Tuỳ chỉnh hiển thị:\n• Cỡ chữ\n• Chiều cao hàng\n• Ẩn/hiện cột\n• Export Excel'
                        : 'Tuỳ chỉnh hiển thị:\n• Cỡ chữ\n• Chiều cao hàng\n• Ẩn/hiện cột',
            },
        ]

        if (pageType === 'member') {
            // Add create button step for member view
            commonSteps.splice(1, 0, {
                selector: '[data-tour="create-business-btn"]',
                title: 'Tạo đơn công tác',
                description:
                    'Click để tạo đơn công tác mới:\n• Chọn loại công tác\n• Chọn thời gian\n• Nhập mục đích\n• Gửi đơn chờ duyệt',
            })

            // Add actions step for member view
            commonSteps.splice(4, 0, {
                selector: '[data-tour="business-data-table"]',
                title: 'Thao tác với đơn công tác',
                description:
                    'Thực hiện các thao tác với đơn công tác:\n\n**Huỷ đơn:**\n• Click icon hành động (⋮) ở cuối hàng\n• Chọn "Huỷ"\n• Xác nhận huỷ\n\n*Chỉ thao tác được với đơn "Đang chờ"',
            })
        } else {
            // Add bulk approve steps for HRM view (3 steps)
            commonSteps.splice(
                3,
                0,
                // Step 1: Select rows
                {
                    selector: '[data-tour="business-data-table"]',
                    title: 'Bước 1: Chọn phiếu công tác',
                    description:
                        'Tick vào checkbox để chọn các phiếu cần duyệt:\n• Click checkbox đầu hàng\n• Có thể chọn nhiều phiếu cùng lúc\n• Chỉ phiếu "Đang chờ" hoặc "Leader đã duyệt" mới chọn được',
                    demo: 'select-business-rows',
                },
                // Step 2: Click approve button
                {
                    selector: '[data-tour="business-data-table"]',
                    title: 'Bước 2: Bấm duyệt nhanh',
                    description:
                        'Sau khi chọn phiếu, thanh Selection sẽ hiện:\n• Hiển thị số phiếu đã chọn\n• Click "Duyệt công tác" để tiếp tục',
                    demo: 'click-bulk-approve-business-button',
                },
                // Step 3: Preview and confirm
                {
                    selector: '[data-tour="business-data-table"]',
                    title: 'Bước 3: Xem và xác nhận',
                    description:
                        'Modal hiện ra với danh sách đơn đã chọn:\n• Kiểm tra lại thông tin\n• Nhập phản hồi (nếu cần)\n• Click "Xác nhận" để hoàn tất',
                    demo: 'bulk-approve-business-modal',
                }
            )
        }

        return commonSteps
    }

    const getVisibleSteps = useCallback(() => {
        return getTourSteps().filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [pageType])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useBusinessListTour
